/**
 * Encaixe da imagem do mapa na grade da cena.
 *
 * A grade da cena é fixa (células inteiras a partir do canto do tabuleiro); quem se
 * ajusta é a imagem, que ocupa um retângulo medido em células (`x`, `y`, `w`, `h`) e é
 * esticada para preenchê-lo. Assim tokens, névoa e desenhos não saem do lugar quando a
 * imagem é alinhada, e mudar o tamanho da célula em px não desalinha nada.
 *
 * Como no original (o "ajuste milimétrico" e o "sincronizador de grid" da cena), há três
 * jeitos de encaixar:
 * - ao carregar: se a imagem tem uma grade desenhada, ela é detectada e a nossa grade
 *   cai em cima dela; senão a imagem é esticada para um número inteiro de células;
 * - à mão: mais/menos na largura e na altura, setas para mover (em centésimos de célula)
 *   e giro (em centésimos de grau);
 * - marcando células da imagem: o tamanho e a posição saem das marcas (e, se a imagem tem
 *   linhas de grade perto delas, das linhas).
 *
 * A grade desenhada pode estar levemente girada em relação à imagem (meio grau já tira
 * uma grade de 30 células do lugar nos cantos): a detecção mede o giro, e a imagem é
 * girada ao contrário em volta do centro do retângulo.
 *
 * Tudo aqui é puro (sem DOM): a leitura dos pixels fica em `imageGrid.ts`.
 */

/**
 * Retângulo da imagem no tabuleiro, em células (pode ser fracionário e começar antes do 0),
 * e o giro aplicado à imagem em volta do centro dele (`angle`, em graus, no sentido do
 * relógio, como no SVG; sem ele, 0).
 */
export type BgRect = { x: number; y: number; w: number; h: number; angle?: number };

/** Grade achada na imagem, em px da imagem: tamanho da célula e onde cai a primeira linha. */
export type ImageGrid = { cellW: number; cellH: number; phaseX: number; phaseY: number };

/** Em que perfil a grade apareceu: linhas escuras, linhas claras ou bordas (casas alternadas). */
export type LineKind = 'dark' | 'light' | 'edges';
export const LINE_KINDS: readonly LineKind[] = ['dark', 'light', 'edges'];

/**
 * Posições em px contínuos da imagem (o pixel i ocupa [i, i + 1)), como a cena usa. No
 * perfil de linhas, o índice i é o pixel i (meio dele em i + 0,5); no de bordas, é a
 * fronteira entre os pixels i − 1 e i (em i).
 */
const KIND_OFFSET: Record<LineKind, number> = { dark: 0.5, light: 0.5, edges: 0 };

export type ImageSize = { width: number; height: number };

/** Limite de colunas e linhas do tabuleiro (o mesmo do modelo). */
export const MAX_CELLS = 400;
/** Folga, em células, para erro de medida: uma sobra menor que isto (~1 px) não conta. */
export const FIT_TOLERANCE = 0.02;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
/** Leva para (-0,5; 0,5]: a sobra de célula fica dos dois lados, nunca mais de meia. */
export const wrapHalf = (v: number): number => {
  const r = v - Math.round(v);
  return r <= -0.5 ? r + 1 : r;
};
/** Arredonda para centésimos de milésimo de célula (o NDB guarda o número como texto). */
export const roundCells = (v: number): number => Math.round(v * 100_000) / 100_000 || 0;

const radians = (deg: number) => (deg * Math.PI) / 180;

/** Metades (em células) do retângulo alinhado que a imagem girada cobre inteiro. */
function innerHalves(rect: BgRect): { a: number; b: number } {
  const t = Math.abs(radians(rect.angle ?? 0));
  const c = Math.cos(t);
  const s = Math.sin(t);
  return { a: Math.max(0, (rect.w / 2) * c - (rect.h / 2) * s), b: Math.max(0, (rect.h / 2) * c - (rect.w / 2) * s) };
}

/** O pedaço alinhado do tabuleiro que a imagem (girada) cobre por inteiro, em células. */
export function innerRect(rect: BgRect): { x: number; y: number; w: number; h: number } {
  const { a, b } = innerHalves(rect);
  return { x: rect.x + rect.w / 2 - a, y: rect.y + rect.h / 2 - b, w: 2 * a, h: 2 * b };
}

/** A caixa alinhada que a imagem (girada) ocupa, em células. */
export function outerRect(rect: BgRect): { x: number; y: number; w: number; h: number } {
  const t = Math.abs(radians(rect.angle ?? 0));
  const a = (rect.w / 2) * Math.cos(t) + (rect.h / 2) * Math.sin(t);
  const b = (rect.h / 2) * Math.cos(t) + (rect.w / 2) * Math.sin(t);
  return { x: rect.x + rect.w / 2 - a, y: rect.y + rect.h / 2 - b, w: 2 * a, h: 2 * b };
}

/** Sem grade na imagem: estica para o número inteiro de células mais perto do tamanho natural. */
export function rectByRounding(size: ImageSize, gridSize: number): BgRect {
  return {
    x: 0,
    y: 0,
    w: clamp(Math.round(size.width / gridSize), 1, MAX_CELLS),
    h: clamp(Math.round(size.height / gridSize), 1, MAX_CELLS),
  };
}

/**
 * Com a grade da imagem: cada célula dela vira uma célula nossa. A primeira linha inteira da
 * imagem cai no canto do tabuleiro; o pedaço de célula antes dela fica para fora (a imagem
 * começa no canto ou antes, nunca depois, para não sobrar grade sem imagem).
 *
 * `grid` é medida na imagem já girada por `angle` (como ela aparece na cena). Girada, a
 * imagem deixa um cantinho sem cobrir nas bordas; se ele invade a primeira coluna (ou
 * linha), o tabuleiro começa na linha seguinte.
 */
export function rectFromImageGrid(size: ImageSize, grid: ImageGrid, angle = 0): BgRect {
  const w = size.width / grid.cellW;
  const h = size.height / grid.cellH;
  const { a, b } = innerHalves({ x: 0, y: 0, w, h, angle });
  /** Quanto de célula vem antes da primeira linha coberta: em [0, 1) mais o cantinho do giro. */
  const lead = (phase: number, cell: number, margin: number) => {
    let f = (((phase / cell) % 1) + 1) % 1;
    if (f > 1 - FIT_TOLERANCE) f -= 1;
    return f < margin - FIT_TOLERANCE ? f + 1 : f;
  };
  return {
    x: roundCells(-lead(grid.phaseX, grid.cellW, w / 2 - a)),
    y: roundCells(-lead(grid.phaseY, grid.cellH, h / 2 - b)),
    w: roundCells(w),
    h: roundCells(h),
    ...(angle ? { angle: roundAngle(angle) } : {}),
  };
}

/** Giro guardado em milésimos de grau. */
export const roundAngle = (deg: number): number => Math.round(deg * 1000) / 1000 || 0;

/**
 * Realinha sem pular células: das posições equivalentes (mesma grade, deslocada por
 * células inteiras), a mais perto de onde a imagem estava. Assim o que já está no
 * tabuleiro continua sobre o mesmo pedaço do mapa.
 */
export function keepNear(rect: BgRect, current: BgRect): BgRect {
  return { ...rect, x: roundCells(rect.x + Math.round(current.x - rect.x)), y: roundCells(rect.y + Math.round(current.y - rect.y)) };
}

/**
 * Tabuleiro dentro da imagem: do canto (0, 0) até a última coluna (e linha) que a imagem
 * (girada, se for o caso) cobre inteira. O pedaço de célula que sobra na borda da imagem
 * fica fora da grade.
 */
export function boardFor(rect: BgRect): { cols: number; rows: number } {
  const inner = innerRect(rect);
  return {
    cols: clamp(Math.floor(inner.x + inner.w + FIT_TOLERANCE), 1, MAX_CELLS),
    rows: clamp(Math.floor(inner.y + inner.h + FIT_TOLERANCE), 1, MAX_CELLS),
  };
}

/* ------------------------------------------------------------------ detecção */

/**
 * Perfil das bordas verticais (por coluna) e horizontais (por linha) de uma imagem em tons
 * de cinza: a soma da diferença entre vizinhos. Linhas de grade viram picos regulares.
 */
export function edgeProfiles(gray: ArrayLike<number>, width: number, height: number): { cols: Float64Array; rows: Float64Array } {
  const cols = new Float64Array(width);
  const rows = new Float64Array(height);
  for (let y = 0; y < height; y += 1) {
    const row = y * width;
    for (let x = 0; x < width; x += 1) {
      const v = gray[row + x]!;
      if (x > 0) cols[x] = cols[x]! + Math.abs(v - gray[row + x - 1]!);
      if (y > 0) rows[y] = rows[y]! + Math.abs(v - gray[row - width + x]!);
    }
  }
  for (let x = 0; x < width; x += 1) cols[x] = cols[x]! / height;
  for (let y = 0; y < height; y += 1) rows[y] = rows[y]! / width;
  return { cols, rows };
}

/**
 * Linhas finas, por coluna (`…X`) e por linha (`…Y`): a fração dos pixels que é vale (mais
 * escuro que os vizinhos a `d` px dos dois lados, por mais de `delta`) ou crista (mais
 * claro). Uma linha de grade passa de ponta a ponta sobre qualquer fundo; a arte do mapa
 * (paredes, móveis) só em pedaços, e o resto se anula. Vizinhos a 2 px e um contraste
 * mínimo também ignoram os blocos de 8 px da compressão JPEG.
 */
export function lineProfiles(gray: ArrayLike<number>, width: number, height: number, d = 2, delta = 7) {
  const darkX = new Float64Array(width);
  const lightX = new Float64Array(width);
  const darkY = new Float64Array(height);
  const lightY = new Float64Array(height);
  for (let y = 0; y < height; y += 1) {
    const row = y * width;
    for (let x = 0; x < width; x += 1) {
      const v = gray[row + x]!;
      if (x >= d && x + d < width) {
        const a = gray[row + x - d]!;
        const b = gray[row + x + d]!;
        if (Math.min(a, b) - v > delta) darkX[x] = darkX[x]! + 1;
        else if (v - Math.max(a, b) > delta) lightX[x] = lightX[x]! + 1;
      }
      if (y >= d && y + d < height) {
        const a = gray[row - d * width + x]!;
        const b = gray[row + d * width + x]!;
        if (Math.min(a, b) - v > delta) darkY[y] = darkY[y]! + 1;
        else if (v - Math.max(a, b) > delta) lightY[y] = lightY[y]! + 1;
      }
    }
  }
  for (let x = 0; x < width; x += 1) {
    darkX[x] = darkX[x]! / height;
    lightX[x] = lightX[x]! / height;
  }
  for (let y = 0; y < height; y += 1) {
    darkY[y] = darkY[y]! / width;
    lightY[y] = lightY[y]! / width;
  }
  return { darkX, lightX, darkY, lightY };
}

/**
 * Linhas regulares achadas num perfil: período e fase (px); `score`, quanto o sinal passa
 * do acaso (em desvios); `hits` de `expected` linhas batidas entre a primeira e a última
 * achadas, e `coverage`, a parte do perfil que esse trecho cobre; `unique`, quanto o pico
 * passa do segundo maior do período (grade tem um pico só; degraus de um degradê ou uma
 * textura miúda, vários do mesmo tamanho).
 */
export type Lattice = { period: number; phase: number; score: number; hits: number; expected: number; coverage: number; unique: number };

function stats(p: ArrayLike<number>): { mean: number; std: number } {
  let sum = 0;
  for (let i = 0; i < p.length; i += 1) sum += p[i]!;
  const mean = sum / p.length;
  let sq = 0;
  for (let i = 0; i < p.length; i += 1) sq += (p[i]! - mean) ** 2;
  return { mean, std: Math.sqrt(sq / p.length) };
}

/** Tira a média da vizinhança (`win` px para cada lado): o claro-escuro geral da arte não conta. */
function detrend(p: ArrayLike<number>, win: number): Float64Array {
  const n = p.length;
  const prefix = new Float64Array(n + 1);
  for (let i = 0; i < n; i += 1) prefix[i + 1] = prefix[i]! + p[i]!;
  const out = new Float64Array(n);
  for (let i = 0; i < n; i += 1) {
    const a = Math.max(0, i - win);
    const b = Math.min(n, i + win + 1);
    out[i] = p[i]! - (prefix[b]! - prefix[a]!) / (b - a);
  }
  return out;
}

/**
 * Dobra o perfil no período em fatias de 1 px e devolve a fatia mais forte: quanto
 * passa da média, por período, somando as duas vizinhas (aguenta linha grossa e um pouco
 * de erro no período sem diluir linha fina).
 */
function fold(p: ArrayLike<number>, period: number, mean: number, sums: Float64Array, counts: Float64Array): { bin: number; value: number; second: number } {
  const bins = Math.ceil(period);
  sums.fill(0, 0, bins);
  counts.fill(0, 0, bins);
  for (let i = 0; i < p.length; i += 1) {
    const b = Math.floor(i % period);
    sums[b] = sums[b]! + p[i]!;
    counts[b] = counts[b]! + 1;
  }
  const values = new Float64Array(bins);
  let best = -Infinity;
  let bin = 0;
  for (let b = 0; b < bins; b += 1) {
    const l = (b - 1 + bins) % bins;
    const r = (b + 1) % bins;
    const c = counts[l]! + counts[b]! + counts[r]!;
    // energia de 3 fatias por período, menos o que o fundo daria nelas
    const v = c ? ((sums[l]! + sums[b]! + sums[r]!) * 3) / c - 3 * mean : 0;
    values[b] = v;
    if (v > best) {
      best = v;
      bin = b;
    }
  }
  // o segundo pico, a pelo menos um quarto de período do primeiro
  let second = -Infinity;
  for (let b = 0; b < bins; b += 1) {
    const d = Math.abs(b - bin);
    if (Math.min(d, bins - d) >= period / 4) second = Math.max(second, values[b]!);
  }
  return { bin, value: best, second };
}

/**
 * Acerta período e fase pelas próprias linhas: o centro de cada pico perto de onde a
 * linha devia estar, e uma reta (posição × número da linha) por esses centros.
 */
function refine(p: ArrayLike<number>, period: number, phase: number, mean: number, std: number): { period: number; phase: number; hits: number; expected: number; coverage: number } {
  let P = period;
  let F = phase;
  let ks: number[] = [];
  let xs: number[] = [];
  for (let pass = 0; pass < 3; pass += 1) {
    // a primeira passada procura longe; as outras, já perto da reta, ignoram ruído
    const half = Math.max(1.5, pass === 0 ? P / 4 : P / 10);
    const nextK: number[] = [];
    const nextX: number[] = [];
    for (let k = Math.ceil(-F / P); F + k * P < p.length; k += 1) {
      const center = F + k * P;
      const lo = Math.max(0, Math.ceil(center - half));
      const hi = Math.min(p.length - 1, Math.floor(center + half));
      if (hi - lo < 1) continue;
      let peak = -Infinity;
      for (let i = lo; i <= hi; i += 1) peak = Math.max(peak, p[i]!);
      if (peak < mean + std) continue;
      // centro do pico: média ponderada do que passa da metade da altura dele
      const floor = (peak + mean) / 2;
      let w = 0;
      let wx = 0;
      for (let i = lo; i <= hi; i += 1) {
        const v = p[i]! - floor;
        if (v > 0) {
          w += v;
          wx += v * i;
        }
      }
      if (w > 0) {
        nextK.push(k);
        nextX.push(wx / w);
      }
    }
    if (nextK.length < 2) break;
    ks = nextK;
    xs = nextX;
    // reta por mínimos quadrados: x = F + k·P
    const n = ks.length;
    const mk = ks.reduce((a, b) => a + b, 0) / n;
    const mx = xs.reduce((a, b) => a + b, 0) / n;
    let num = 0;
    let den = 0;
    for (let i = 0; i < n; i += 1) {
      num += (ks[i]! - mk) * (xs[i]! - mx);
      den += (ks[i]! - mk) ** 2;
    }
    if (den <= 0) break;
    P = num / den;
    F = mx - P * mk;
  }
  const hits = ks.length;
  // linhas esperadas: só no trecho da grade (há mapas com borda sem grade)
  const expected = hits ? ks[hits - 1]! - ks[0]! + 1 : 0;
  const coverage = hits ? (xs[hits - 1]! - xs[0]!) / p.length : 0;
  return { period: P, phase: ((F % P) + P) % P, hits, expected, coverage };
}

/**
 * Procura linhas regulares num perfil com período entre `minPeriod` e `maxPeriod` (px).
 * A força de cada período é o pico da dobra medido contra o acaso, que cai com a raiz do
 * número de repetições: o dobro do período tem o mesmo sinal com metade das repetições, e
 * a metade, metade do sinal; o período certo ganha dos dois.
 */
export function findLattice(p0: ArrayLike<number>, minPeriod: number, maxPeriod: number): Lattice | null {
  const n = p0.length;
  const hiP = Math.min(maxPeriod, n / 2);
  if (n < 8 || minPeriod > hiP) return null;
  const p = detrend(p0, Math.max(8, Math.round(hiP)));
  const { mean, std } = stats(p);
  if (std <= 1e-9) return null;
  const sums = new Float64Array(Math.ceil(hiP) + 2);
  const counts = new Float64Array(Math.ceil(hiP) + 2);
  let best: { period: number; bin: number; score: number; unique: number } | null = null;
  // passo pequeno o bastante para a última linha errar menos de 1 px
  for (let period = Math.max(2, minPeriod); period <= hiP; period += Math.max(0.002, (0.8 * period) / n)) {
    const { bin, value, second } = fold(p, period, mean, sums, counts);
    const score = (value / std) * Math.sqrt(n / period / 3);
    if (!best || score > best.score) best = { period, bin, score, unique: value > 0 ? 1 - Math.max(0, second) / value : 0 };
  }
  if (!best || best.score <= 0) return null;
  const refined = refine(p, best.period, best.bin + 0.5, mean, std);
  if (!Number.isFinite(refined.period) || refined.period < minPeriod * 0.9 || refined.period > hiP * 1.1) return null;
  return { ...refined, score: best.score, unique: best.unique };
}

/**
 * Medida fina pelo periodograma: o perfil projetado no seno e no cosseno do período (e de
 * algumas harmônicas, que reforçam linha fina). Somando todas as linhas, mede posição com
 * fração de px mesmo com linha fraca (uma linha sozinha some no desenho do mapa), e sem
 * depender de fatias. A fase sai do ângulo da fundamental.
 */
const HARMONICS = 4;
function periodogram(p: ArrayLike<number>, period: number, withPhase = true): { phase: number; power: number } {
  const re = new Float64Array(HARMONICS);
  const im = new Float64Array(HARMONICS);
  let power = 0;
  for (let h = 1; h <= HARMONICS; h += 1) {
    // gira um fasor a cada amostra (sem chamar seno e cosseno por amostra)
    const step = (2 * Math.PI * h) / period;
    const [cs, sn] = [Math.cos(step), Math.sin(step)];
    let [c, s] = [1, 0];
    let a = 0;
    let b = 0;
    for (let i = 0; i < p.length; i += 1) {
      a += p[i]! * c;
      b += p[i]! * s;
      [c, s] = [c * cs - s * sn, s * cs + c * sn];
    }
    re[h - 1] = a;
    im[h - 1] = b;
    power += a * a + b * b;
  }
  if (!withPhase) return { phase: 0, power };
  // fase: onde a soma das harmônicas (o formato de uma linha fina) é máxima
  const at = (phi: number) => {
    let v = 0;
    for (let h = 1; h <= HARMONICS; h += 1) {
      const t = (2 * Math.PI * h * phi) / period;
      v += re[h - 1]! * Math.cos(t) + im[h - 1]! * Math.sin(t);
    }
    return v;
  };
  const stepPhi = period / Math.max(64, Math.ceil(period * 8));
  let best = 0;
  let bestV = -Infinity;
  for (let phi = 0; phi < period; phi += stepPhi) {
    const v = at(phi);
    if (v > bestV) {
      bestV = v;
      best = phi;
    }
  }
  const l = at(best - stepPhi);
  const r = at(best + stepPhi);
  const d = l - 2 * bestV + r;
  const phase = best + (d < 0 ? ((l - r) / (2 * d)) * stepPhi : 0);
  return { phase: ((phase % period) + period) % period, power };
}

/**
 * Período e fase com fração de px, entre `minPeriod` e `maxPeriod` (uma faixa estreita,
 * em volta do que a detecção ou as marcas acharam).
 */
export function fineLattice(p0: ArrayLike<number>, minPeriod: number, maxPeriod: number): { period: number; phase: number } | null {
  const n = p0.length;
  if (n < 8 || !(minPeriod > 1) || maxPeriod < minPeriod) return null;
  const p = detrend(p0, Math.max(8, Math.round(maxPeriod)));
  const scan = (lo: number, hi: number, step: number) => {
    let best: { period: number; power: number } | null = null;
    for (let period = lo; period <= hi + 1e-9; period += step) {
      const { power } = periodogram(p, period, false);
      if (!best || power > best.power) best = { period, power };
    }
    return best;
  };
  // grosso (a última linha anda meio px de um passo para o outro), depois fino perto do
  // melhor (0,05 px)
  const coarse = Math.max(0.002, (0.5 * minPeriod) / n);
  const rough = scan(minPeriod, maxPeriod, coarse);
  if (!rough) return null;
  const fine = scan(Math.max(minPeriod, rough.period - 2 * coarse), Math.min(maxPeriod, rough.period + 2 * coarse), coarse / 10);
  const period = fine?.period ?? rough.period;
  return { period, phase: periodogram(p, period).phase };
}

/** Fase (px) das linhas de período `period` num perfil. */
function phaseOf(p0: ArrayLike<number>, period: number): number {
  return periodogram(detrend(p0, Math.max(8, Math.round(period))), period).phase;
}

/** Perfil de colunas (`x`) ou de linhas (`y`) de um tipo de linha numa imagem. */
function profileOf(gray: ArrayLike<number>, width: number, height: number, kind: LineKind, axis: 'x' | 'y'): ArrayLike<number> {
  if (kind === 'edges') {
    const e = edgeProfiles(gray, width, height);
    return axis === 'x' ? e.cols : e.rows;
  }
  const l = lineProfiles(gray, width, height);
  return kind === 'dark' ? (axis === 'x' ? l.darkX : l.darkY) : axis === 'x' ? l.lightX : l.lightY;
}

/**
 * Giro da grade desenhada (graus, sentido do relógio): mede a fase das linhas verticais em
 * faixas horizontais (e das horizontais em faixas verticais); com a grade girada, a fase
 * anda de uma faixa para a outra, e a inclinação dá o ângulo. Para alinhar, a imagem gira
 * o contrário.
 */
export function measureRotation(gray: ArrayLike<number>, width: number, height: number, grid: ImageGrid, kind: LineKind, bands = 6): number {
  const slope = (points: [number, number][], period: number): number | null => {
    if (points.length < 3) return null;
    // desenrola a fase (ela dá a volta no período)
    const ys = [points[0]![1]];
    for (let i = 1; i < points.length; i += 1) {
      let v = points[i]![1];
      while (v - ys[i - 1]! > period / 2) v -= period;
      while (v - ys[i - 1]! < -period / 2) v += period;
      ys.push(v);
    }
    const xs = points.map((q) => q[0]);
    const mx = xs.reduce((a, b) => a + b, 0) / xs.length;
    const my = ys.reduce((a, b) => a + b, 0) / ys.length;
    let num = 0;
    let den = 0;
    for (let i = 0; i < xs.length; i += 1) {
      num += (xs[i]! - mx) * (ys[i]! - my);
      den += (xs[i]! - mx) ** 2;
    }
    return den > 0 ? num / den : null;
  };
  const vertical: [number, number][] = [];
  const horizontal: [number, number][] = [];
  for (let b = 0; b < bands; b += 1) {
    // faixa de linhas da imagem: fase das linhas verticais
    const y0 = Math.round((b * height) / bands);
    const y1 = Math.round(((b + 1) * height) / bands);
    if (y1 - y0 > 8) {
      const typed = gray as Partial<Float32Array>;
      const band = typed.subarray ? typed.subarray(y0 * width, y1 * width) : Array.prototype.slice.call(gray, y0 * width, y1 * width);
      vertical.push([(y0 + y1) / 2, phaseOf(profileOf(band, width, y1 - y0, kind, 'x'), grid.cellW)]);
    }
    // faixa de colunas: fase das linhas horizontais
    const x0 = Math.round((b * width) / bands);
    const x1 = Math.round(((b + 1) * width) / bands);
    if (x1 - x0 > 8) {
      const w = x1 - x0;
      const band = new Float32Array(w * height);
      for (let y = 0; y < height; y += 1) for (let x = 0; x < w; x += 1) band[y * w + x] = gray[y * width + x0 + x]!;
      horizontal.push([(x0 + x1) / 2, phaseOf(profileOf(band, w, height, kind, 'y'), grid.cellH)]);
    }
  }
  // girada no sentido do relógio: a linha vertical vai para a esquerda descendo (dx/dy = -tg),
  // a horizontal desce indo para a direita (dy/dx = tg)
  const sx = slope(vertical, grid.cellW);
  const sy = slope(horizontal, grid.cellH);
  if (sx === null || sy === null) return 0;
  return (Math.atan((sy - sx) / 2) * 180) / Math.PI;
}

/** Força mínima das linhas (desvios acima do acaso) em cada sentido, e a do sentido mais forte. */
const GRID_MIN_SCORE = 5;
/** O pico da grade tem de ser o único do período (o segundo, no máximo metade dele). */
const GRID_MIN_UNIQUE = 0.5;
const GRID_STRONG_SCORE = 7;

/** Células por lado: no mínimo (o período máximo procurado) e linhas achadas no lado maior. */
const GRID_MIN_CELLS = 8;
const GRID_MIN_LINES_LONG = 12;
/**
 * Mapa pequeno (4 a 7 células por lado): poucas repetições, como a arte que se repete; só
 * vale com prova forte nos dois sentidos — pico bem único, todas as linhas, mesmo tamanho.
 */
const SMALL_MIN_CELLS = 4;
const SMALL_MIN_UNIQUE = 0.85;
const SMALL_MIN_SCORE = 6;
const isSmallGridLattice = (l: Lattice | null): l is Lattice =>
  !!l && l.score >= SMALL_MIN_SCORE && l.unique >= SMALL_MIN_UNIQUE && l.hits >= SMALL_MIN_CELLS - 1 && l.hits >= l.expected && l.coverage >= 0.5;

/** Linhas que contam como grade: fortes, regulares e presentes em boa parte da imagem. */
const isGridLattice = (l: Lattice | null): l is Lattice =>
  !!l && l.score >= GRID_MIN_SCORE && l.hits >= 6 && l.hits >= 0.7 * l.expected && l.coverage >= 0.4 && l.unique >= GRID_MIN_UNIQUE;

/**
 * A grade desenhada numa imagem (tons de cinza, `width` × `height` px), ou null quando não
 * há uma clara. Procura em três perfis (linhas escuras, linhas claras e bordas, que pegam
 * casas de cores alternadas) e só aceita quando o mesmo perfil dá, nos dois sentidos,
 * células quase quadradas (até 4% de diferença) e muitas linhas: a arte também se repete
 * (colunas, estantes), mas poucas vezes; grade de mapa tem dezenas de células. Mapa com
 * menos de 8 células num lado (ou 12 no maior) passa por um teste mais duro
 * (`isSmallGridLattice`), e com menos de 4 fica sem detecção.
 */
export function detectImageGrid(gray: ArrayLike<number>, width: number, height: number, minCell = 12): (ImageGrid & { kind: LineKind }) | null {
  const all = profilesByKind(gray, width, height);
  let best: { grid: ImageGrid & { kind: LineKind }; score: number } | null = null;
  for (const kind of LINE_KINDS) {
    const [px, py] = all[kind];
    const lx = findLattice(px, minCell, width / GRID_MIN_CELLS);
    const ly = findLattice(py, minCell, height / GRID_MIN_CELLS);
    if (!isGridLattice(lx) || !isGridLattice(ly)) continue;
    if (Math.max(lx.score, ly.score) < GRID_STRONG_SCORE || Math.max(lx.hits, ly.hits) < GRID_MIN_LINES_LONG) continue;
    if (Math.abs(lx.period / ly.period - 1) > 0.04) continue;
    const score = Math.min(lx.score, ly.score);
    const off = KIND_OFFSET[kind];
    if (!best || score > best.score) best = { grid: { cellW: lx.period, cellH: ly.period, phaseX: lx.phase + off, phaseY: ly.phase + off, kind }, score };
  }
  if (best) return best.grid;
  // mapa pequeno: células grandes demais para a procura acima
  for (const kind of LINE_KINDS) {
    const [px, py] = all[kind];
    // até um pouco mais que 4 células por lado (o período exato de 4 fica dentro da busca)
    const lx = findLattice(px, Math.max(minCell, width / GRID_MIN_CELLS), width / (SMALL_MIN_CELLS - 0.5));
    const ly = findLattice(py, Math.max(minCell, height / GRID_MIN_CELLS), height / (SMALL_MIN_CELLS - 0.5));
    if (!isSmallGridLattice(lx) || !isSmallGridLattice(ly)) continue;
    if (Math.max(lx.score, ly.score) < GRID_STRONG_SCORE || Math.abs(lx.period / ly.period - 1) > 0.03) continue;
    const score = Math.min(lx.score, ly.score);
    const off = KIND_OFFSET[kind];
    if (!best || score > best.score) best = { grid: { cellW: lx.period, cellH: ly.period, phaseX: lx.phase + off, phaseY: ly.phase + off, kind }, score };
  }
  return best?.grid ?? null;
}

/** Os perfis (colunas, linhas) de cada tipo de linha. */
function profilesByKind(gray: ArrayLike<number>, width: number, height: number): Record<LineKind, [ArrayLike<number>, ArrayLike<number>]> {
  const edges = edgeProfiles(gray, width, height);
  const lines = lineProfiles(gray, width, height);
  return { dark: [lines.darkX, lines.darkY], light: [lines.lightX, lines.lightY], edges: [edges.cols, edges.rows] };
}

/* ------------------------------------------------------------------ marcas */

/** Célula marcada pelo mestre sobre a imagem, em px da imagem. */
export type CellMark = { left: number; top: number; right: number; bottom: number };

/**
 * Tamanho e primeira linha a partir das bordas marcadas: cada borda é uma linha da grade.
 * As bordas são ordenadas e numeradas uma a uma (o passo até a próxima, em células), e uma
 * reta pelos pontos dá o tamanho exato; com mais marcas, mais preciso.
 */
export function axisFromEdges(edges: number[], approxCell: number): { cell: number; phase: number } | null {
  const sorted = [...edges].filter(Number.isFinite).sort((a, b) => a - b);
  if (sorted.length < 2 || !(approxCell > 0)) return null;
  let cell = approxCell;
  const index = [0];
  for (let i = 1; i < sorted.length; i += 1) {
    index.push(index[i - 1]! + Math.round((sorted[i]! - sorted[i - 1]!) / cell));
    // reta pelos pontos até aqui: o tamanho fica mais certo a cada borda mais longe
    const first = index[0]!;
    const last = index[i]!;
    if (last > first) {
      const n = i + 1;
      const mk = index.reduce((a, b) => a + b, 0) / n;
      const mx = sorted.slice(0, n).reduce((a, b) => a + b, 0) / n;
      let num = 0;
      let den = 0;
      for (let j = 0; j < n; j += 1) {
        num += (index[j]! - mk) * (sorted[j]! - mx);
        den += (index[j]! - mk) ** 2;
      }
      if (den > 0 && num > 0) cell = num / den;
    }
  }
  if (!(cell > 0)) return null;
  // primeira linha: a média das bordas "voltadas" para a mesma fase
  let sx = 0;
  let sy = 0;
  for (const e of sorted) {
    const a = (2 * Math.PI * e) / cell;
    sx += Math.cos(a);
    sy += Math.sin(a);
  }
  const phase = (((Math.atan2(sy, sx) / (2 * Math.PI)) * cell) % cell + cell) % cell;
  return { cell, phase };
}

/** A grade que as marcas descrevem (em px da imagem). */
export function gridFromMarks(marks: CellMark[]): ImageGrid | null {
  const ok = marks.filter((m) => m.right - m.left > 1 && m.bottom - m.top > 1);
  if (!ok.length) return null;
  const median = (v: number[]) => {
    const s = [...v].sort((a, b) => a - b);
    return s.length % 2 ? s[(s.length - 1) / 2]! : (s[s.length / 2 - 1]! + s[s.length / 2]!) / 2;
  };
  const mw = median(ok.map((m) => m.right - m.left));
  const mh = median(ok.map((m) => m.bottom - m.top));
  const x = axisFromEdges(ok.flatMap((m) => [m.left, m.right]), mw);
  const y = axisFromEdges(ok.flatMap((m) => [m.top, m.bottom]), mh);
  if (!x || !y) return null;
  return { cellW: x.cell, cellH: y.cell, phaseX: x.phase, phaseY: y.phase };
}

/**
 * Acerta pelas linhas da imagem uma grade aproximada: procura linhas regulares com período
 * até `range` diferente (6%: o que as marcas do mestre erram; menos, para medir de novo
 * na resolução cheia o que a detecção achou na imagem reduzida), nos perfis de `kinds`.
 * Sem linhas claras, fica a aproximada.
 */
export function snapToImageLines(approx: ImageGrid, gray: ArrayLike<number>, width: number, height: number, kinds: readonly LineKind[] = LINE_KINDS, range = 0.06): ImageGrid {
  const all = profilesByKind(gray, width, height);
  const axis = (which: 0 | 1, cell: number, phase: number): { period: number; phase: number } | null => {
    let best: { period: number; phase: number; score: number } | null = null;
    for (const kind of kinds) {
      const p = all[kind][which];
      // as linhas têm de existir de verdade (força e regularidade)…
      const l = findLattice(p, cell * (1 - range), cell * (1 + range));
      if (!l || l.score < 4 || l.hits < 3 || l.hits < 0.6 * l.expected || l.unique < GRID_MIN_UNIQUE) continue;
      const off = KIND_OFFSET[kind];
      // a fase das linhas pode estar meia célula fora da aproximada só se ela errou feio
      if (Math.abs(wrapHalf((l.phase + off - phase) / l.period)) > 0.25) continue;
      // …e a medida é a fina (periodograma)
      const fine = fineLattice(p, cell * (1 - range), cell * (1 + range));
      if (fine && (!best || l.score > best.score)) best = { period: fine.period, phase: fine.phase + off, score: l.score };
    }
    return best;
  };
  const lx = axis(0, approx.cellW, approx.phaseX);
  const ly = axis(1, approx.cellH, approx.phaseY);
  return {
    cellW: lx?.period ?? approx.cellW,
    cellH: ly?.period ?? approx.cellH,
    phaseX: lx?.phase ?? approx.phaseX,
    phaseY: ly?.phase ?? approx.phaseY,
  };
}
